/**
 * AI slash command handlers.
 *
 * /rewrite  -- rewrite selected/current block text
 * /expand   -- expand bullet point to full paragraph
 * /summarise -- condense block to brief summary
 * /translate -- toggle NL <-> EN
 *
 * All use CF Workers AI via the existing API worker.
 */

import type { BlockNoteEditor } from '@blocknote/core';

import { getAuthHeaders } from '../auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8788';

// ---- Helpers ---------------------------------------------------------------

function showToast(message: string, type: 'success' | 'error' = 'success') {
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    bottom: 24px;
    right: 24px;
    padding: 12px 20px;
    background: ${type === 'success' ? '#37352f' : '#e03131'};
    color: #fff;
    border-radius: 8px;
    font-size: 14px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    transition: opacity 0.3s ease;
  `;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

/**
 * Extract plain text from the current block.
 */
function getCurrentBlockText(editor: BlockNoteEditor): string {
  const block = editor.getTextCursorPosition().block;
  if (!block || !block.content) return '';

  const contentArray = block.content as Array<{ type: string; text?: string }>;
  return contentArray
    .filter((item) => item.type === 'text' && item.text)
    .map((item) => item.text)
    .join('');
}

/**
 * Replace the content of the current block with new text.
 */
function replaceCurrentBlockText(editor: BlockNoteEditor, newText: string): void {
  const { block } = editor.getTextCursorPosition();
  if (!block) return;

  editor.updateBlock(block, {
    content: [{ type: 'text', text: newText, styles: {} }],
  });
}

/**
 * Call AI endpoint for text transformation.
 * Falls back to a simple prompt-based approach via the API worker.
 */
async function callAI(prompt: string, text: string): Promise<string> {
  try {
    const response = await fetch(`${API_URL}/v1/ai/transform`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ prompt, text }),
    });

    if (!response.ok) {
      // If the AI endpoint doesn't exist yet, show a helpful message
      if (response.status === 404) {
        throw new Error('AI endpoint not available yet. Deploy /v1/ai/transform to enable.');
      }
      throw new Error(`AI request failed: ${response.status}`);
    }

    const data = await response.json();
    return data.result || data.text || text;
  } catch (err) {
    console.error('AI call failed:', err);
    throw err;
  }
}

// ---- Command handlers ------------------------------------------------------

export async function rewriteBlock(editor: BlockNoteEditor): Promise<void> {
  const text = getCurrentBlockText(editor);
  if (!text.trim()) {
    showToast('Geen tekst om te herschrijven', 'error');
    return;
  }

  showToast('Herschrijven...');
  try {
    const result = await callAI(
      'Rewrite the following text to improve clarity and flow. Keep the same meaning and language. Return only the rewritten text.',
      text,
    );
    replaceCurrentBlockText(editor, result);
    showToast('Tekst herschreven');
  } catch {
    showToast('Herschrijven mislukt', 'error');
  }
}

export async function expandBlock(editor: BlockNoteEditor): Promise<void> {
  const text = getCurrentBlockText(editor);
  if (!text.trim()) {
    showToast('Geen tekst om uit te breiden', 'error');
    return;
  }

  showToast('Uitbreiden...');
  try {
    const result = await callAI(
      'Expand this bullet point or short note into a full, well-written paragraph. Keep the same language. Return only the expanded text.',
      text,
    );
    replaceCurrentBlockText(editor, result);
    showToast('Tekst uitgebreid');
  } catch {
    showToast('Uitbreiden mislukt', 'error');
  }
}

export async function summariseBlock(editor: BlockNoteEditor): Promise<void> {
  const text = getCurrentBlockText(editor);
  if (!text.trim()) {
    showToast('Geen tekst om samen te vatten', 'error');
    return;
  }

  showToast('Samenvatten...');
  try {
    const result = await callAI(
      'Condense this text into a brief 1-2 sentence summary. Keep the same language. Return only the summary.',
      text,
    );
    replaceCurrentBlockText(editor, result);
    showToast('Tekst samengevat');
  } catch {
    showToast('Samenvatten mislukt', 'error');
  }
}

export async function translateBlock(editor: BlockNoteEditor): Promise<void> {
  const text = getCurrentBlockText(editor);
  if (!text.trim()) {
    showToast('Geen tekst om te vertalen', 'error');
    return;
  }

  showToast('Vertalen...');
  try {
    const result = await callAI(
      'If this text is in Dutch, translate it to English. If it is in English, translate it to Dutch. Return only the translated text.',
      text,
    );
    replaceCurrentBlockText(editor, result);
    showToast('Tekst vertaald');
  } catch {
    showToast('Vertalen mislukt', 'error');
  }
}
